import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Select, { GroupBase, OptionsOrGroups } from 'react-select';
import { useResourceInfo } from '../../hooks/useResourceInfo';
import { HiClock } from 'react-icons/hi';

/**
 * Resource interface for dropdown items
 */
interface ResourceOption {
    id: string | number;
    name: string;
    email?: string;
    skills?: string[];
    is_deleted?: boolean;
}

/**
 * Select option format
 */
interface SelectOption {
    value: string;
    label: string;
}

/**
 * Group option format for react-select
 */
interface GroupedOption {
    label: string;
    options: SelectOption[];
}

/**
 * Props for the ResourceMultiSelectWithRecommendations component
 */
interface ResourceMultiSelectWithRecommendationsProps {
    id: string;
    name: string;
    label?: string;
    selectedIds?: string[];  // Array of selected resource IDs
    placeholder?: string;
    onChange: (selectedIds: string[]) => void;  // Returns array of IDs
    projectMembers: ResourceOption[];
    skillId?: string | number;
    projectMemberIds?: (string | number)[];
    className?: string;
    required?: boolean;
    showAsterisk?: boolean;
    disabled?: boolean;
    disabledMessage?: string;
    excludeIds?: (string | number)[];
    projectId?: string;
    taskId?: string;
}

/**
 * ResourceMultiSelectWithRecommendations Component
 * 
 * A multi-select searchable dropdown component that shows:
 * 1. Project members at the top
 * 2. A separator with "Recommended Resources (Have skill, not in project)"
 * 3. Organization resources that have the matching skill but are NOT in the project
 */
const ResourceMultiSelectWithRecommendations: React.FC<ResourceMultiSelectWithRecommendationsProps> = ({
    id,
    name,
    label,
    selectedIds = [],
    placeholder = 'Select additional assignees',
    onChange,
    projectMembers = [],
    skillId,
    projectMemberIds = [],
    className = '',
    required = false,
    showAsterisk = false,
    disabled = false,
    disabledMessage,
    excludeIds = [],
    projectId,
    taskId,
}) => {
    // Get all organization resources from Redux (manage_resources API data)
    const { data: allOrgResources = [] } = useSelector((state: any) => state.resources);
    const resourcesData = useSelector((state: any) => state.resources.resourcesData);
    const { getResourceName } = useResourceInfo();

    // Note: API call is made by parent component (TaskTableModal) to avoid duplicate calls
    // This component just reads from Redux cache

    // Get recommended resources: from API or fallback to skill-based filtering
    const recommendedResources = useMemo(() => {
        const projectMemberIdSet = new Set(
            projectMemberIds.map(id => String(id))
        );
        const excludeIdSet = new Set(
            excludeIds.map(id => String(id))
        );

        // If we have projectId and taskId, use backend API recommendations
        if (projectId && taskId) {
            const key = `recommendedResources_${projectId}_${taskId}`;
            const apiData = resourcesData[key];
            
            if (apiData && typeof apiData === 'object' && !Array.isArray(apiData)) {
                // API response format: { resourceID: [task objects] }
                // Filter out resources that are already in the work unit (project members)
                const resources: Array<{ id: string | number; name: string; taskCount: number }> = [];
                
                Object.keys(apiData).forEach((resourceId) => {
                    const tasks = apiData[resourceId];
                    const resourceIdStr = String(resourceId);
                    
                    if (Array.isArray(tasks)) {
                        const taskCount = tasks.length;
                        // Skip if resource is already in project (work unit) or explicitly excluded
                        if (!projectMemberIdSet.has(resourceIdStr) && !excludeIdSet.has(resourceIdStr)) {
                            // Get resource from allOrgResources to check skills
                            const resource = allOrgResources.find((r: ResourceOption) => String(r.id) === resourceIdStr);
                            
                            // Filter by skill if skillId is provided
                            if (skillId && resource) {
                                if (!resource.skills || !Array.isArray(resource.skills) || !resource.skills.includes(String(skillId))) {
                                    return; // Skip resources that don't have the required skill
                                }
                            } else if (skillId && !resource) {
                                return; // Skip if we can't verify skill and skillId is required
                            }
                            
                            const resourceName = resource?.name || getResourceName(resourceId) || `Resource ${resourceId}`;
                            
                            resources.push({
                                id: resourceId,
                                name: resourceName,
                                taskCount: taskCount,
                            });
                        }
                    }
                });
                
                // Sort by task count ascending (0 active tasks first), then by name
                return resources.sort((a, b) => {
                    if (a.taskCount !== b.taskCount) {
                        return a.taskCount - b.taskCount;
                    }
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });
            }
        }
        return [];
    }, [allOrgResources, skillId, projectMemberIds, excludeIds, projectId, taskId, resourcesData, getResourceName]);

    // Filter project members (exclude deleted, excluded IDs, and filter by skill)
    const filteredProjectMembers = useMemo(() => {
        const excludeIdSet = new Set(excludeIds.map(id => String(id)));
        const selectedIdSet = new Set(selectedIds.map(id => String(id)));

        return projectMembers
            .filter((r: ResourceOption) => {
                // Skip deleted resources
                if (r.is_deleted) return false;
                // Skip excluded resources
                if (excludeIdSet.has(String(r.id))) return false;
                // Skip already selected options
                if (selectedIdSet.has(String(r.id))) return false;
                // Filter by skill if skillId is provided
                if (skillId && r.skills && Array.isArray(r.skills)) {
                    return r.skills.includes(String(skillId));
                }
                // If no skillId or no skills array, include if no skillId filter
                return !skillId;
            })
            .sort((a: ResourceOption, b: ResourceOption) =>
                (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
            );
    }, [projectMembers, excludeIds, skillId, selectedIds]);

    // Convert to react-select format with groups
    const groupedOptions: OptionsOrGroups<SelectOption, GroupBase<SelectOption>> = useMemo(() => {
        const groups: GroupedOption[] = [];

        // Add project members group
        if (filteredProjectMembers.length > 0) {
            // Get API data for task counts if available
            const apiKey = projectId && taskId ? `recommendedResources_${projectId}_${taskId}` : null;
            const apiData = apiKey ? resourcesData[apiKey] : null;
            
            groups.push({
                label: 'Work Unit Members',
                options: filteredProjectMembers.map(r => {
                    const resourceIdStr = String(r.id);
                    // Look up task count from API data if available
                    let taskCount = 0;
                    if (apiData && typeof apiData === 'object' && apiData[resourceIdStr]) {
                        const tasks = apiData[resourceIdStr];
                        if (Array.isArray(tasks)) {
                            taskCount = tasks.length;
                        }
                    }
                    
                    // If we have API data, show task count; otherwise just show name
                    if (apiData) {
                        const resourceName = r.email ? `${r.name} (${r.email})` : r.name;
                        const taskCountText = `${taskCount} ${taskCount === 1 ? 'active task' : 'active tasks'}`;
                        return {
                            value: resourceIdStr,
                            label: `${resourceName}|||${taskCountText}`,
                            taskCount: taskCount,
                            resourceName: resourceName,
                        };
                    } else {
                        return {
                            value: resourceIdStr,
                            label: r.email ? `${r.name} (${r.email})` : r.name,
                        };
                    }
                }).sort((a: any, b: any) => {
                    // Sort by task count ascending when we have API data
                    if (a.taskCount !== undefined && b.taskCount !== undefined) {
                        if (a.taskCount !== b.taskCount) {
                            return a.taskCount - b.taskCount;
                        }
                    }
                    return 0;
                }),
            });
        }

        // Add recommended resources group with descriptive text (only if there are any)
        if (recommendedResources.length > 0) {
            groups.push({
                label: 'Recommended Resources',
                options: recommendedResources.map((r: any) => {
                    // Check if this is from API (has taskCount) or from skill-based (has email)
                    if (r.taskCount !== undefined) {
                        // Format: Resource Name                        X active tasks
                        const resourceName = r.name || `Resource ${r.id}`;
                        const taskCountText = `${r.taskCount} ${r.taskCount === 1 ? 'active task' : 'active tasks'}`;
                        return {
                            value: String(r.id),
                            label: `${resourceName}|||${taskCountText}`,
                            taskCount: r.taskCount,
                            resourceName: resourceName,
                        };
                    } else {
                        // Fallback to original format for skill-based recommendations
                        return {
                            value: String(r.id),
                            label: r.email ? `${r.name} (${r.email})` : r.name,
                        };
                    }
                }),
            });
        }

        return groups;
    }, [filteredProjectMembers, recommendedResources, projectId, taskId]);

    // Filter selectedIds to remove any that are in excludeIds
    const cleanedSelectedIds = useMemo(() => {
        if (!selectedIds || selectedIds.length === 0) return [];
        const excludeIdSet = new Set(excludeIds.map(id => String(id)));
        return selectedIds.filter(id => !excludeIdSet.has(String(id)));
    }, [selectedIds, excludeIds]);

    // Compute selected options from IDs by looking up in all available resources
    // Note: We look in ALL resources (not filtered) to find the labels for selected IDs
    const selectedOptions: SelectOption[] = useMemo(() => {
        if (!cleanedSelectedIds || cleanedSelectedIds.length === 0) return [];

        const selectedIdSet = new Set(cleanedSelectedIds.map(id => String(id)));
        const options: SelectOption[] = [];

        // Look in project members (unfiltered - we use projectMembers directly)
        projectMembers.forEach(r => {
            if (selectedIdSet.has(String(r.id)) && !r.is_deleted) {
                options.push({
                    value: String(r.id),
                    label: r.email ? `${r.name} (${r.email})` : r.name,
                });
            }
        });

        // Look in recommended resources (may have taskCount for API-based recommendations)
        recommendedResources.forEach((r: any) => {
            if (selectedIdSet.has(String(r.id)) && !options.find(o => o.value === String(r.id))) {
                // For selected values, show clean name without task count
                options.push({
                    value: String(r.id),
                    label: r.email ? `${r.name} (${r.email})` : r.name,
                });
            }
        });

        // Also check in all org resources for any other selected IDs
        allOrgResources.forEach((r: ResourceOption) => {
            if (selectedIdSet.has(String(r.id)) && !options.find(o => o.value === String(r.id))) {
                options.push({
                    value: String(r.id),
                    label: r.email ? `${r.name} (${r.email})` : r.name,
                });
            }
        });

        return options;
    }, [cleanedSelectedIds, projectMembers, recommendedResources, allOrgResources]);

    // When excludeIds changes, notify parent to update if any selected IDs were removed
    React.useEffect(() => {
        if (selectedIds && selectedIds.length > 0 && cleanedSelectedIds.length !== selectedIds.length) {
            // Some IDs were filtered out due to excludeIds, notify parent
            onChange(cleanedSelectedIds);
        }
    }, [excludeIds]); // Only run when excludeIds changes

    const portalTarget = typeof document !== 'undefined' ? document.body : undefined;

    // Handle change - convert SelectOption[] back to string[] IDs
    const handleChange = (newValue: readonly SelectOption[] | null) => {
        const ids = (newValue || []).map(option => option.value);
        onChange(ids);
    };

    // Wrapper component for disabled tooltip
    const SelectWrapper = ({ children }: { children: React.ReactNode }) => {
        if (disabled && disabledMessage) {
            return (
                <div className="relative group">
                    {children}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                        {disabledMessage}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                    </div>
                </div>
            );
        }
        return <>{children}</>;
    };

    return (
        <div className="mb-4">
            {/* Label with optional required indicator */}
            {label && (
                <label
                    htmlFor={id}
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    {label}
                    {showAsterisk && required && <span className="ml-1 text-red-500">*</span>}
                </label>
            )}

            <div className="mt-2">
                {/* React-Select component with custom styling */}
                <SelectWrapper>
                    <Select
                        id={id}
                        name={name}
                        isMulti
                        menuPortalTarget={portalTarget}
                        menuPosition="fixed"
                        value={selectedOptions}
                        onChange={handleChange}
                        options={groupedOptions}
                        placeholder={placeholder}
                        isDisabled={disabled}
                        className={`w-full ${className}`}
                        classNamePrefix="multiselect"
                        formatOptionLabel={(option: any, { context }: any) => {
                            // Check if label contains our separator (API recommendations with task count)
                            if (option.label && typeof option.label === 'string' && option.label.includes('|||')) {
                                const [resourceName, taskCountText] = option.label.split('|||');
                                return (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minWidth: '400px', gap: '16px' }}>
                                        <span style={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}>{resourceName}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgb(107 114 128)', flexShrink: 0, whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                                            <HiClock style={{ fontSize: '14px', flexShrink: 0 }} />
                                            {taskCountText}
                                        </span>
                                    </div>
                                );
                            }
                            return option.label || '';
                        }}
                        theme={(theme) => ({
                            ...theme,
                            borderRadius: 8,
                            colors: {
                                ...theme.colors,
                                // Primary colors for focus and selection states
                                primary: '#3b82f6',
                                primary75: '#60a5fa',
                                primary50: '#93c5fd',
                                primary25: '#bfdbfe',
                                // Neutral colors for light mode
                                neutral0: 'rgb(249 250 251)', // Background
                                neutral5: 'rgb(243 244 246)', // Hover state
                                neutral10: 'rgb(229 231 235)', // Active state
                                neutral20: '#6b7280', // Border
                                neutral30: '#9ca3af', // Disabled
                                neutral40: '#d1d5db', // Placeholder
                                neutral50: '#e5e7eb',
                                neutral60: '#f3f4f6',
                                neutral70: '#f9fafb',
                                neutral80: '#ffffff',
                                neutral90: '#ffffff',
                            },
                        })}
                        styles={{
                            // Main control container
                            control: (base, state) => ({
                                ...base,
                                minHeight: '44px',
                                padding: '2px 4px',
                                backgroundColor: 'rgb(249 250 251)',
                                borderColor: 'rgb(209 213 219)',
                                borderRadius: '0.5rem',
                                boxShadow: 'none',
                                '&:hover': {
                                    borderColor: 'rgb(156 163 175)',
                                },
                                '&:focus-within': {
                                    borderColor: '#3b82f6',
                                    boxShadow: '0 0 0 1px #3b82f6',
                                },
                                // Dark mode styles
                                '.dark &': {
                                    backgroundColor: '#1e293b',
                                    borderColor: 'rgb(55 65 81)',
                                }
                            }),

                            // Container for selected values and input
                            valueContainer: (base) => ({
                                ...base,
                                padding: '2px 8px',
                            }),

                            // Input field
                            input: (base) => ({
                                ...base,
                                color: 'rgb(17 24 39)', // Dark text for light mode
                                margin: '2px',
                                padding: '2px',
                                '.dark &': {
                                    color: 'white', // White text for dark mode
                                }
                            }),

                            // Render dropdown menu in portal with high z-index
                            menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999,
                            }),

                            // Dropdown menu
                            menu: (base) => ({
                                ...base,
                                backgroundColor: 'rgb(249 250 251)', // Light mode default
                                border: '1px solid rgb(209 213 219)',
                                borderRadius: '0.5rem',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                marginTop: '4px',
                                minWidth: '450px',
                                '.dark &': {
                                    backgroundColor: '#374151',
                                    border: 'none',
                                }
                            }),

                            // Menu list container
                            menuList: (base) => ({
                                ...base,
                                padding: '4px',
                            }),

                            // Group heading style - distinct from options
                            groupHeading: (base) => ({
                                ...base,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: 'rgb(107 114 128)', // gray-500
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                padding: '8px 12px 4px 12px',
                                backgroundColor: 'transparent',
                            }),

                            // Individual option in dropdown
                            option: (base, state) => ({
                                ...base,
                                padding: '10px 14px',
                                fontSize: '0.8rem',
                                // Light mode colors
                                backgroundColor: state.isSelected
                                    ? 'rgb(229 231 235)'
                                    : state.isFocused
                                        ? 'rgb(229 231 235)'
                                        : 'transparent',
                                color: 'rgb(17 24 39)',
                                '&:hover': {
                                    backgroundColor: 'rgb(229 231 235)',
                                },
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                // Dark mode colors
                                '.dark &': {
                                    backgroundColor: state.isSelected
                                        ? '#4b5563'
                                        : state.isFocused
                                            ? '#4b5563'
                                            : 'transparent',
                                    color: state.isSelected || state.isFocused
                                        ? 'white'
                                        : 'rgb(229 231 235)',
                                    '&:hover': {
                                        backgroundColor: '#4b5563',
                                        color: 'white',
                                    },
                                }
                            }),

                            // Selected value tag
                            multiValue: (base) => ({
                                ...base,
                                backgroundColor: '#4b5563',
                                borderRadius: '0.375rem',
                                margin: '2px',
                            }),

                            // Selected value tag text
                            multiValueLabel: (base) => ({
                                ...base,
                                color: 'white',
                                padding: '4px 8px',
                                fontSize: '0.75rem',
                            }),

                            // Remove button for selected value
                            multiValueRemove: (base) => ({
                                ...base,
                                color: 'white',
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: '#374151',
                                    color: 'white',
                                },
                                borderRadius: '0 0.375rem 0.375rem 0',
                            }),

                            // Placeholder text
                            placeholder: (base) => ({
                                ...base,
                                color: 'rgb(156 163 175)',
                                fontSize: '0.875rem',
                            }),

                            // Selected value text (for single select mode, kept for consistency)
                            singleValue: (base) => ({
                                ...base,
                                color: 'white',
                            }),

                            // Clear all button
                            clearIndicator: (base) => ({
                                ...base,
                                padding: '4px',
                            }),

                            // Dropdown indicator
                            dropdownIndicator: (base) => ({
                                ...base,
                                padding: '4px',
                            }),

                            // Group styles
                            group: (base) => ({
                                ...base,
                                paddingTop: '4px',
                                paddingBottom: '4px',
                            }),
                        }}
                    />
                </SelectWrapper>
            </div>
        </div>
    );
};

export default ResourceMultiSelectWithRecommendations;
